import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import Slider from 'react-slick';
import Image from '@empiriecom/module-components/Image';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { FragmentConfiguration } from '@/config';
import { MediaAsset } from '@/src/interfaces';
import useDebounce from '@empiriecom/module-components/hooks/useDebounce';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import { useGalleryContext } from '../GalleryContext';
import { useProduct } from '../../ProductContext';
import addPlayerInstance from '../../ExternalServices/Cliplister/addPlayerInstance';

const StyledSlide = styled.div<{ mobile: boolean }>`
  height: ${({ mobile }) => (mobile ? '23.4375rem' : '41.25rem')};
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
`;

const SlideContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
  height: 100%;
  position: relative;
`;

const StyledImage = styled(Image)`
  margin: auto;
`;

const StyledViewerWrapper = styled.div`
  width: 100%;
`;

const CliplisterPreviewWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.75);
`;

const CliplisterPlayIconWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  border-radius: 50%;
  width: 4rem;
  height: 4rem;
  background-color: rgba(0, 0, 0, 0.75);
`;

const PlayIcon = getIcon('Play');

const StyledPlayIcon = styled(PlayIcon)`
  pointer-events: none;
`;

interface GallerySliderProps {
  /** Array containing the product image source(s). */
  images: MediaAsset[];
  /** The product name. */
  name: string;
}

export const handleSlideClick = (
  images: MediaAsset[],
  getActiveImage: () => number,
  cliplisterViewerAdded: boolean,
  setIsZoom: (flag: boolean) => void,
  orderNumber: string,
  customerId: string,
  setCliplisterViewerAdded: (flag: boolean) => void,
  setShowPreview: (flag: boolean) => void,
) => {
  if (images[getActiveImage()].type !== 'CLIPLISTER') {
    setIsZoom(true);
  } else if (!cliplisterViewerAdded && images[getActiveImage()].type === 'CLIPLISTER') {
    addPlayerInstance(orderNumber, customerId);
    setCliplisterViewerAdded(true);
    setShowPreview(false);
  }
};

export const GallerySlider: FunctionComponent<GallerySliderProps> = ({
  images,
  name,
}): ReactElement => {
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [cliplisterViewerAdded, setCliplisterViewerAdded] = useState<boolean>(false);
  const {
    product: { orderNumber },
  } = useProduct();
  const {
    externalServices: {
      cliplister: { customerId },
    },
  } = useConfig();
  const {
    getCurrentSlider,
    setCurrentSlider,
    getActiveImage,
    setActiveImage,
    setIsZoom,
  } = useGalleryContext();
  const device = useMedia({ desktop: 'desktop', mobile: 'mobile', tablet: 'desktop' });
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const config = useConfig<FragmentConfiguration>();
  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    getCurrentSlider()?.slickGoTo(getActiveImage());
  }, [getActiveImage()]);

  const getSettings = (
    isTouchDevice: boolean,
    defaultSettings = {
      arrows: false,
      dots: false,
      infinite: false,
      slidesToScroll: 1,
      slidesToShow: 1,
      speed: 300,
      swipe: false,
    },
  ) => {
    if (isTouchDevice) {
      return {
        ...defaultSettings,
        beforeChange: (_oldIndex: number, newIndex: number) => {
          if (
            typeof (global as any).clViewer !== 'undefined' &&
            typeof (global as any).clViewer.pause === 'function' &&
            (global as any).clViewer.isPlaying()
          ) {
            (global as any).clViewer.pause();
          }
          setActiveImage(newIndex);
        },
        swipe: true,
        vertical: false,
      };
    }

    return {
      ...defaultSettings,
      afterChange: (index: number) => {
        if (index > images.length - 1 || index === 0) {
          setActiveImage(0);
        } else {
          setActiveImage(index);
        }
      },
      beforeChange: () => {
        if (
          typeof (global as any).clViewer !== 'undefined' &&
          typeof (global as any).clViewer.pause === 'function' &&
          (global as any).clViewer.isPlaying()
        ) {
          (global as any).clViewer.pause();
        }
      },
      fade: true,
    };
  };

  const debouncedCliplisterAdded = useDebounce(cliplisterViewerAdded, 750);

  useEffect(() => {
    if (debouncedCliplisterAdded && typeof (global as any).clViewer !== 'undefined') {
      let twentyficePercentTracked = false;
      let fiftyPercentTracked = false;
      let seventyfivePercentTracked = false;
      let onehundredPercentTracked = false;
      if (!(global as any).clViewer.isPlaying()) {
        (global as any).clViewer.play();
      }
      (global as any).clViewer.onPlay(() => {
        dispatchGTMEvent({
          event: 'ga_event',
          eventAction: 'viewVideo',
          eventCategory: 'cliplister',
          eventLabel: 'detailview',
          eventValue: 'start',
        });
        const trackingInterval = setInterval(() => {
          if ((global as any).clViewer.isPlaying()) {
            if ((global as any).clViewer.getProgressPlayed() > 25 && !twentyficePercentTracked) {
              dispatchGTMEvent({
                event: 'ga_event',
                eventAction: 'viewVideo',
                eventCategory: 'cliplister',
                eventLabel: 'detailview',
                eventValue: '25',
              });
              twentyficePercentTracked = true;
            }
            if ((global as any).clViewer.getProgressPlayed() > 50 && !fiftyPercentTracked) {
              dispatchGTMEvent({
                event: 'ga_event',
                eventAction: 'viewVideo',
                eventCategory: 'cliplister',
                eventLabel: 'detailview',
                eventValue: '50',
              });
              fiftyPercentTracked = true;
            }
            if ((global as any).clViewer.getProgressPlayed() > 75 && !seventyfivePercentTracked) {
              dispatchGTMEvent({
                event: 'ga_event',
                eventAction: 'viewVideo',
                eventCategory: 'cliplister',
                eventLabel: 'detailview',
                eventValue: '75',
              });
              seventyfivePercentTracked = true;
            }
            if ((global as any).clViewer.getProgressPlayed() === 100 && !onehundredPercentTracked) {
              dispatchGTMEvent({
                event: 'ga_event',
                eventAction: 'viewVideo',
                eventCategory: 'cliplister',
                eventLabel: 'detailview',
                eventValue: '100',
              });
              onehundredPercentTracked = true;
            }
          } else {
            clearInterval(trackingInterval);
          }
        }, 250);
      });
    }
  }, [debouncedCliplisterAdded]);

  const settings = getSettings(isMobile);

  const slides: ReactElement[] = [];

  images.map(
    // eslint-disable-next-line array-callback-return, consistent-return
    (image: MediaAsset) => {
      if (image.type === 'IMAGE') {
        return slides.push(
          <StyledSlide
            data-testid="image-slide"
            key={image.url}
            mobile={isMobile}
            onClick={() => {
              handleSlideClick(
                images,
                getActiveImage,
                cliplisterViewerAdded,
                setIsZoom,
                orderNumber,
                customerId,
                setCliplisterViewerAdded,
                setShowPreview,
              );
            }}
          >
            <SlideContent>
              <StyledImage
                alt={name}
                src={`${image.url}?$${config.galleryProductImageFormat[device]}$`}
              />
            </SlideContent>
          </StyledSlide>,
        );
      }
      if (image.type === 'CLIPLISTER') {
        return slides.push(
          <StyledSlide
            className="cliplister-slide"
            key={image.url}
            mobile={isMobile}
            data-testid="cliplister-slide"
            onClick={() => {
              handleSlideClick(
                images,
                getActiveImage,
                cliplisterViewerAdded,
                setIsZoom,
                orderNumber,
                customerId,
                setCliplisterViewerAdded,
                setShowPreview,
              );
            }}
          >
            <SlideContent>
              <StyledViewerWrapper id={`cliplister-viewer-${orderNumber}`} />
              {showPreview && (
                <CliplisterPreviewWrapper>
                  <StyledImage alt={name} src={image.url} />
                  {images[getActiveImage()].type === 'CLIPLISTER' && (
                    <CliplisterPlayIconWrapper>
                      <StyledPlayIcon size="2.5rem" color="#fff" />
                    </CliplisterPlayIconWrapper>
                  )}
                </CliplisterPreviewWrapper>
              )}
            </SlideContent>
          </StyledSlide>,
        );
      }
    },
  );

  return (
    <div data-testid="gallery-slider">
      <Slider
        /* disable linting warning for prop spreading as settings need to be different for mobile and desktop */
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...settings}
        ref={(slider) => setCurrentSlider(slider)}
        initialSlide={getActiveImage()}
      >
        {slides}
      </Slider>
    </div>
  );
};

export default GallerySlider;
